import { useState, type ReactNode } from 'react';

import {
  AsyncSection,
  DataTable,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  Tabs,
  TextField,
  type Column,
  type Tab,
} from '../../components';
import { Permission, type EsocialDiagnosticResponse, type EsocialStageResponse } from '../../api';
import { formatDate } from '../../format';
import { useRequiredSession } from '../../session';
import { useCertificates, useCompanies, useEsocialDiagnostics } from '../queries';
import {
  CERTIFICATE_STATUS_LABELS,
  ENVIRONMENT_LABELS,
  ESOCIAL_COPY,
  OUTCOME_LABELS,
  OUTCOME_TONES,
  STAGE_LABELS,
  requestFailureMessage,
  stageMessage,
} from './esocial';
import { EsocialPortalWalkPanel } from './EsocialPortalWalkPanel';
import { BROWSER_BACKEND_LABELS } from './portalWalk';
import { ESOCIAL_TABS, ESOCIAL_TABS_LABEL, type EsocialTab } from './esocialTabs';

/**
 * The connection to eSocial, as vyttech staff prove it works.
 *
 * Its own screen rather than a panel on `/equipe/certificado-digital`, and the
 * two addresses say why: that screen answers "what does the platform sign as,
 * and until when?", and this one answers "can we reach the government right
 * now?". The second is asked on a bad morning, by whoever is on shift -- which
 * is not necessarily the administrator who answers the first.
 *
 * Three decisions shape it.
 *
 * **The company is optional, and the form says so first.** The two stages that
 * need no company are the ones somebody setting a deployment up needs, and they
 * need them before there is a customer to select. A required selector would put
 * the least useful case first and block the most useful one.
 *
 * **A failed test is a successful request.** The answer *is* the diagnosis, so
 * the result renders as three rows rather than as an error banner. The banner
 * is kept for the request itself failing -- no client configured, a company
 * with no CNPJ -- which means the diagnostic never ran, and is a different
 * thing to tell somebody.
 *
 * **Every stage is shown, including the ones that did not run.** `Não
 * executada` is a neutral tone on purpose: a stage skipped because the one
 * before it failed is a consequence, not a second problem, and three red rows
 * for one fault is how a screen teaches people to stop reading it. The fourth
 * stage, the browser, is a separate channel and follows only the certificate,
 * so a failed handshake and a working portal can sit side by side.
 *
 * Since #452 the page is two tabs, the section in `?aba=`: `Conexão` is the
 * diagnostic exactly as described above, and `Portal eSocial` is the portal
 * walk of #451 -- start one, read its stages and screens, download the run.
 * Only the active tab is mounted, so opening the page asks nothing of the walk
 * endpoints and opening the walk asks nothing of the diagnostic's.
 */
export function EsocialPage() {
  const content: Record<EsocialTab, () => ReactNode> = {
    conexao: () => <EsocialConnection />,
    portal: () => <EsocialPortalWalkPanel />,
  };
  const tabs: readonly Tab[] = (Object.keys(ESOCIAL_TABS) as EsocialTab[]).map((slug) => ({
    slug,
    label: ESOCIAL_TABS[slug],
    content: content[slug],
  }));

  return (
    <div className="page">
      <PageHeader title={ESOCIAL_COPY.title} description={ESOCIAL_COPY.description} />
      <Tabs tabs={tabs} label={ESOCIAL_TABS_LABEL} />
    </div>
  );
}

/** The connection diagnostic: the page as it was before it had tabs. */
function EsocialConnection() {
  const session = useRequiredSession();
  const companies = useCompanies();
  const diagnostics = useEsocialDiagnostics();
  const [companyId, setCompanyId] = useState('');
  const [browserUrl, setBrowserUrl] = useState('');

  // #285's endpoint is `certificates:read`, which the administrator holds
  // alone. An analyst holds `esocial:read` and would get a 403 from it, so the
  // panel is not mounted for them -- offering it would be offering a refusal,
  // which is the same rule the sidebar follows. What an analyst does see is the
  // certificate summary the diagnostic itself carries, which is the same public
  // metadata and is what makes a failed certificate stage actionable.
  const mayReadCertificates = session.principal.permissions.includes(Permission.CERTIFICATES_READ);

  const columns: readonly Column<EsocialStageResponse>[] = [
    { key: 'stage', header: ESOCIAL_COPY.stage, render: (stage) => STAGE_LABELS[stage.stage] },
    {
      key: 'outcome',
      header: ESOCIAL_COPY.outcome,
      render: (stage) => (
        <span className={`badge badge--${OUTCOME_TONES[stage.outcome]}`}>
          {OUTCOME_LABELS[stage.outcome]}
        </span>
      ),
    },
    {
      key: 'detail',
      header: ESOCIAL_COPY.detail,
      render: (stage) => (
        <>
          <p>{stageMessage(stage)}</p>
          {/* The server's own sentence, underneath rather than instead of ours:
              it carries the detail ours cannot -- which endpoint, which date,
              which CNPJ -- and it is written for whoever is integrating. */}
          <p className="table__muted">{stage.summary}</p>
        </>
      ),
    },
  ];

  const specific = requestFailureMessage(diagnostics.error);

  return (
    <>
      <Panel title={ESOCIAL_COPY.runTitle} description={ESOCIAL_COPY.runDescription}>
        <Form
          onSubmit={() =>
            diagnostics.mutate({
              companyId: companyId === '' ? null : companyId,
              browserUrl: browserUrl.trim() === '' ? null : browserUrl.trim(),
            })
          }
          aria-label={ESOCIAL_COPY.runTitle}
        >
          <FormError
            error={diagnostics.error}
            {...(specific !== undefined && { message: specific })}
          />
          <SelectField
            label={ESOCIAL_COPY.company}
            value={companyId}
            onChange={setCompanyId}
            options={(companies.data?.items ?? []).map((company) => ({
              value: company.id,
              label: company.name,
            }))}
            placeholder={
              companies.isPending ? ESOCIAL_COPY.companiesLoading : ESOCIAL_COPY.companyNone
            }
            hint={ESOCIAL_COPY.companyHint}
          />
          <TextField
            label={ESOCIAL_COPY.browserUrl}
            value={browserUrl}
            onChange={setBrowserUrl}
            hint={ESOCIAL_COPY.browserUrlHint}
            autoComplete="off"
          />
          <SubmitButton pending={diagnostics.isPending} pendingLabel={ESOCIAL_COPY.running}>
            {ESOCIAL_COPY.run}
          </SubmitButton>
        </Form>
      </Panel>

      {diagnostics.data !== undefined && (
        <Panel title={ESOCIAL_COPY.resultTitle}>
          <DiagnosticResult result={diagnostics.data} columns={columns} />
        </Panel>
      )}

      {mayReadCertificates && <InstalledCertificate />}
    </>
  );
}

function DiagnosticResult({
  result,
  columns,
}: {
  result: EsocialDiagnosticResponse;
  columns: readonly Column<EsocialStageResponse>[];
}) {
  return (
    <>
      <p
        className={`panel__footnote${result.passed ? '' : ' panel__footnote--warning'}`}
        role="status"
      >
        {result.passed ? ESOCIAL_COPY.resultPassed : ESOCIAL_COPY.resultFailed}
      </p>
      <DataTable
        caption={ESOCIAL_COPY.resultCaption}
        columnVisibility={false}
        columns={columns}
        rows={result.stages}
        rowKey={(stage) => stage.stage}
      />
      <dl className="definitions">
        <dt>{ESOCIAL_COPY.environment}</dt>
        <dd>{ENVIRONMENT_LABELS[result.environment]}</dd>
        <dt>{ESOCIAL_COPY.endpoint}</dt>
        <dd>
          <code>{result.endpoint}</code>
        </dd>
        {result.tls_version !== null && result.tls_version !== undefined && (
          <>
            <dt>{ESOCIAL_COPY.tlsVersion}</dt>
            <dd>{result.tls_version}</dd>
          </>
        )}
        {result.employer_cnpj_formatted !== null &&
          result.employer_cnpj_formatted !== undefined && (
            <>
              <dt>{ESOCIAL_COPY.employer}</dt>
              <dd>{result.employer_cnpj_formatted}</dd>
            </>
          )}
        {result.esocial_response_code !== null && result.esocial_response_code !== undefined && (
          <>
            <dt>{ESOCIAL_COPY.esocialResponse}</dt>
            {/* eSocial's own code and wording, untranslated. It is already
                pt-BR, it is the government's rather than ours, and it is what
                an operator quotes when they open a ticket. */}
            <dd>
              {result.esocial_response_code} — {result.esocial_response_message}
            </dd>
          </>
        )}
        {result.events_found !== null && result.events_found !== undefined && (
          <>
            <dt>{ESOCIAL_COPY.eventsFound}</dt>
            <dd>{result.events_found}</dd>
          </>
        )}
        {result.browser_backend && (
          <>
            <dt>{ESOCIAL_COPY.browserBackend}</dt>
            <dd>{BROWSER_BACKEND_LABELS[result.browser_backend]}</dd>
          </>
        )}
        {result.certificate !== null && result.certificate !== undefined && (
          <>
            <dt>{ESOCIAL_COPY.certificateHolder}</dt>
            <dd>{result.certificate.subject_name}</dd>
            <dt>{ESOCIAL_COPY.certificateCnpj}</dt>
            <dd>{result.certificate.subject_cnpj_formatted}</dd>
            <dt>{ESOCIAL_COPY.certificateValidUntil}</dt>
            <dd>{formatDate(result.certificate.not_valid_after)}</dd>
            <dt>{ESOCIAL_COPY.certificateStatus}</dt>
            <dd>{CERTIFICATE_STATUS_LABELS[result.certificate.status]}</dd>
          </>
        )}
      </dl>
      <BrowserScreenshot result={result} />
    </>
  );
}

/**
 * What the browser stage saw, when it ran: the screenshot at a size a person
 * can read, captioned with where the visit ended and the page's title.
 *
 * It travels inline in the response and is kept nowhere else — the audit
 * entry records the address and the status, never the picture.
 */
function BrowserScreenshot({ result }: { result: EsocialDiagnosticResponse }) {
  const screenshot = result.browser_screenshot_png_base64;
  if (screenshot === null || screenshot === undefined) {
    return null;
  }
  return (
    <figure className="screenshot" aria-label={ESOCIAL_COPY.screenshotTitle}>
      <img
        className="screenshot__image"
        src={`data:image/png;base64,${screenshot}`}
        alt={ESOCIAL_COPY.screenshotAlt}
      />
      <figcaption>
        <dl className="definitions">
          <dt>{ESOCIAL_COPY.screenshotFinalUrl}</dt>
          <dd>
            <code>{result.browser_final_url}</code>
          </dd>
          <dt>{ESOCIAL_COPY.screenshotPageTitle}</dt>
          <dd>{result.browser_page_title}</dd>
          {result.browser_http_status !== null && result.browser_http_status !== undefined && (
            <>
              <dt>{ESOCIAL_COPY.screenshotStatus}</dt>
              <dd>{result.browser_http_status}</dd>
            </>
          )}
        </dl>
      </figcaption>
    </figure>
  );
}

/**
 * The stored certificate's own summary, read from #285's endpoint.
 *
 * A component rather than a block, so the hook inside it is only called when
 * the panel is mounted -- which is only for a principal that holds
 * `certificates:read`. Calling it unconditionally and hiding the result would
 * still make the request, and an analyst would spend a `403` on every visit.
 */
function InstalledCertificate() {
  const certificates = useCertificates();

  return (
    <Panel title={ESOCIAL_COPY.certificateTitle} description={ESOCIAL_COPY.certificateDescription}>
      <AsyncSection
        state={certificates}
        loadingLabel={ESOCIAL_COPY.certificateTitle}
        errorTitle={ESOCIAL_COPY.runFailed}
        isEmpty={(data) => data.items.length === 0}
        emptyTitle={ESOCIAL_COPY.certificateNone}
      >
        {(data) => (
          <dl className="definitions">
            {data.items.slice(0, 1).map((certificate) => (
              <div key={certificate.id} className="contents">
                <dt>{ESOCIAL_COPY.certificateHolder}</dt>
                <dd>{certificate.subject_name}</dd>
                <dt>{ESOCIAL_COPY.certificateCnpj}</dt>
                <dd>{certificate.subject_cnpj_formatted}</dd>
                <dt>{ESOCIAL_COPY.certificateValidUntil}</dt>
                <dd>{formatDate(certificate.not_valid_after)}</dd>
                <dt>{ESOCIAL_COPY.certificateStatus}</dt>
                <dd>{CERTIFICATE_STATUS_LABELS[certificate.status]}</dd>
                <dt>{ESOCIAL_COPY.certificateFingerprint}</dt>
                <dd>
                  <code>{certificate.fingerprint_sha256}</code>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </AsyncSection>
    </Panel>
  );
}
